import { useDebounceFn, useMemoizedFn, useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BaseFillExclamation } from '@/app/components/icons/BaseFillExclamation';
import { BaseLineCut } from '@/app/components/icons/BaseLineCut';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { Loading } from '@/app/components/icons/Loading';
import { DisplaySelectedAudioItem } from '@/app/components/shared/DisplaySelectedAudioItem';
import { CharImgCropModal } from '@/app/components/shared/ImgCropModals/CharImgCropModal';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import { ELEMENT_NO_VOICE_ID, VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { Button } from '@/app/components/ui/button';
import { DialogFooter } from '@/app/components/ui/dialog';
import { SITE, TTS_VOICE_LIST, UPLOAD_SCENE } from '@/app/constants';
import { useBatchCrop } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type {
  AudioUploadFile,
  ElementType,
  ImageFormState,
  ImagePrompt,
  SourceVideo,
  UploadFile,
  UploadImageType,
  VideoFormState,
  VideoPrompt,
} from '@/app/types';
import { cn, convertAssets2Components, getMaxImageSize, reportEvent } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { MAX_INPUT_LENGTH, MAX_TEXTAREA_INPUT_LENGTH } from '../constants';
import { useElementDialogApi } from '../hooks/use-element-dialog-api';
import { usePreCreate } from '../hooks/use-pre-create';
import type { MaterialV1Label } from '../types';
import { ChooseLabelSelect } from './ChooseLabelSelect';
import { DialogFooterTip } from './DialogFooterTip';
import { InputField } from './InputField';
import { MediaUploaderWithPreview } from './MediaUploaderWithPreview';
import { StepsBar } from './StepsBar';
import { TextAreaField } from './TextAreaField';

const UPDATE_TIME = 500;
const MAX_IMAGE_COUNT = 3; // 上传槽位总数
const MIN_VIDEO_DURATION = 1;
const MAX_VIDEO_DURATION = 5;
// 类型守卫：判断是否为图片资源
const isImageAsset = (asset: ImagePrompt | VideoPrompt | null): asset is ImagePrompt => {
  return asset !== null && 'sourceImage' in asset;
};

interface MediaElementFormProps {
  /** 初始数据 */
  initialData?: ImageFormState | VideoFormState;
  /** 标签列表 */
  labels: MaterialV1Label[] | undefined;
  /** 添加标签回调 */
  handleAddLabel: (label: MaterialV1Label) => void;
  /** 保存回调 */
  onSave: (element: ElementType) => void;
  /** 是否为编辑模式 */
  isEditMode?: boolean;
}

/**
 * 图片/视频模式的表单组件（内部管理状态）
 * 包含：上传图片/视频、名称、标签、音频、样式、描述等字段
 */
export const MediaElementForm = React.memo<MediaElementFormProps>(
  ({ initialData, labels, handleAddLabel, onSave, isEditMode = false }) => {
    const t = useTranslations();
    const { formParams } = useStore((state) => ({
      formParams: state.character2videoFormParams,
    }));

    const [state, setState] = useSetState<ImageFormState | VideoFormState>(
      initialData || {
        modality: 'image',
        optionalAssets: [null, null, null],
        voice: { id: ELEMENT_NO_VOICE_ID },
      },
    );

    // 判断是否为视频模式
    const isVideoMode = state.modality === 'video';

    // 使用预创建 hook（管理 step 和预创建逻辑）
    const { step, setStep, preCreateLoading, updateCurrentAssets, afterNameCheck } = usePreCreate({
      state,
      isVideoMode,
      onStateUpdate: (updates) => {
        if (isVideoMode) {
          setState(updates as Pick<VideoFormState, keyof VideoFormState>);
        } else {
          setState(updates as Pick<ImageFormState, keyof ImageFormState>);
        }
      },
    });

    // 使用统一的 API hook
    const { nameRef, checkLoading, nameCheckMsg, setNameCheckMsg, checkName, submitLoading, submit } =
      useElementDialogApi({
        state,
        initialName: initialData?.name,
        isEditMode,
        onSave,
        onStateUpdate: (updates) => {
          if (isVideoMode) {
            setState(updates as Pick<VideoFormState, keyof VideoFormState>);
          } else {
            setState(updates as Pick<ImageFormState, keyof ImageFormState>);
          }
        },
      });

    // edit 模式记录初始 optionalAssets，如果没修改，不需要重新 recaption
    useEffect(() => {
      if (isEditMode && initialData?.optionalAssets) {
        updateCurrentAssets(initialData.optionalAssets);
      }
    }, [initialData, updateCurrentAssets, isEditMode]);

    // 上传中的图片索引：state 用于驱动UI更新（loading状态、draggable状态等）
    const [uploadingIndexArr, setUploadingIndexArr] = useState<{ targetIdx: number; fileId: string }[]>([]);
    // 使用 ref 来存储最新的上传索引映射，避免状态更新延迟问题
    const uploadingIndexMapRef = useRef<{ targetIdx: number; fileId: string }[]>([]);
    /** 全屏拖拽多文件时，与槽位内上传共用 handleChangeFiles 的 batch 锚点槽位 */
    const modalDropAnchorRef = useRef<number | null>(null);
    const [preparingCropIndex, setPreparingCropIndex] = useState<number | null>(null);
    const [isAudioDialogOpen, setIsAudioDialogOpen] = useState(false);
    const maxImageSize = useMemo(() => getMaxImageSize(formParams), [formParams]);

    const currentVoice = !isVideoMode ? (state as ImageFormState).voice : undefined;
    useEffect(() => {
      if (state.modality === 'video' || currentVoice) {
        return;
      }

      setState((prev) => {
        if (prev.modality === 'video' || prev.voice) {
          return prev;
        }

        return {
          ...prev,
          voice: { id: ELEMENT_NO_VOICE_ID },
        };
      });
    }, [currentVoice, setState, state.modality]);

    // 局部更新单个资源
    const updateAsset = (idx: number, asset: ImagePrompt | VideoPrompt | null) => {
      setState((prev) => {
        const assets = [...(prev.optionalAssets || [])];
        assets[idx] = asset;
        return { optionalAssets: assets };
      });
    };

    // 根据id更新资源
    const updateAssetsByIds = (assetMap: { id: string; prompt: ImagePrompt | VideoPrompt }[]) => {
      const newAssets = [...(state?.optionalAssets || [])];

      assetMap.forEach(({ id, prompt }) => {
        const index = newAssets.findIndex((item) => {
          if (!item) return false;
          if (isImageAsset(item)) {
            return item.sourceImage === id;
          }
          return item.video === id;
        });
        if (index !== -1) {
          newAssets[index] = prompt;
        }
      });

      setState({ optionalAssets: newAssets });
    };

    const { selectModalImageSource, handleCloseSelectModal, handleOpenSelectModal, handleSaveChangedImagePrompts } =
      // 不开 allowTempMode：本表单没有挂补传 hook，裁剪结果若只存本地会永远传不上去。
      // 当前 ElementDialog 的 7 个创建入口都被 useCheckEnableCreateElement 挡在登录之后，
      // 未登录走不到这里，所以裁剪直接走真实上传是安全的。
      useBatchCrop({
        maxImageSize,
        updateImagePromptByIds: updateAssetsByIds,
        scene: UPLOAD_SCENE.MATERIAL,
      });

    const handleClickCropButton = async (idx: number, asset?: ImagePrompt | VideoPrompt | null) => {
      // 裁剪功能只支持图片，不支持视频
      if (!asset || !isImageAsset(asset)) {
        return;
      }

      try {
        setPreparingCropIndex(idx);
        await handleOpenSelectModal(idx, asset);
      } finally {
        setPreparingCropIndex(null);
      }
    };

    const steps = useMemo(
      () => [
        { name: t('elements.elementDialog.uploadReference'), value: 1 },
        { name: t('elements.elementDialog.reviewDescription'), value: 2 },
      ],
      [t],
    );

    // 查找可用的槽位
    const findAvailableSlot = (idx: number) => {
      const assets = state?.optionalAssets || [];
      const currentMaxCount = isVideoMode ? 1 : MAX_IMAGE_COUNT;

      // 找出所有为null的槽位索引
      const emptySlots: number[] = [];
      for (let i = 0; i < currentMaxCount; i++) {
        if (!assets[i]) {
          emptySlots.push(i);
        }
      }

      // 过滤掉正在上传中的槽位
      const uploadingSlots = new Set(uploadingIndexMapRef.current.map((item) => item.targetIdx));
      const availableSlots = emptySlots.filter((slotIdx) => !uploadingSlots.has(slotIdx));

      if (availableSlots.length === 0) {
        return -1;
      }

      // 优先返回从指定idx开始的第一个可用槽位
      const startFromIdx = availableSlots.find((slotIdx) => slotIdx >= idx);
      return startFromIdx !== undefined ? startFromIdx : availableSlots[0];
    };

    const handleChangeFiles = (
      idx: number,
      uploadFile: UploadFile | AudioUploadFile,
      type: 'image' | 'video' | 'audio',
    ) => {
      if (type === 'audio') return;

      if (uploadFile.status === 'removed') {
        // 清理上传中状态（审核失败取消上传时，需要移除 loading 状态）
        uploadingIndexMapRef.current = uploadingIndexMapRef.current.filter((i) => i.fileId !== uploadFile.id);
        setUploadingIndexArr((prev) => prev.filter((i) => i.fileId !== uploadFile.id));
        // 删除当前
        updateAsset(idx, null);
        return;
      }

      if (uploadFile.status === 'uploading') {
        const isFirstFile = !uploadFile.batchIndex || uploadFile.batchIndex === 0;
        const targetIdx = isFirstFile ? idx : findAvailableSlot(idx);
        if (targetIdx === -1) {
          return;
        }
        const newUploadingItem = { targetIdx, fileId: uploadFile.id };
        // 同时更新 ref 和 state
        uploadingIndexMapRef.current = [...uploadingIndexMapRef.current, newUploadingItem];
        setUploadingIndexArr((prev) => [...prev, newUploadingItem]);
        return;
      }

      // 上传完成 - 先查找 targetIdx，然后再从 ref 中移除
      const targetIdx = uploadingIndexMapRef.current.find((i) => i.fileId === uploadFile.id)?.targetIdx;

      // 从 ref 中移除对应项
      uploadingIndexMapRef.current = uploadingIndexMapRef.current.filter((i) => i.fileId !== uploadFile.id);
      setUploadingIndexArr((prev) => prev.filter((i) => i.fileId !== uploadFile.id));

      if (uploadFile.status === 'error') {
        // 可以考虑显示具体错误信息给用户
        console.error(`Upload failed for file ${uploadFile.id}`);
        return;
      }

      // 上传成功，更新资源
      if (targetIdx !== undefined && targetIdx >= 0) {
        // 如果是 video，需要转换
        if (type === 'video' && uploadFile.file) {
          const video = uploadFile.file as SourceVideo;
          updateAsset(targetIdx, {
            video: video.src,
            videoPreview: video.preview,
            duration: video.duration,
            coverImage: video.cover_url,
            coverImagePreview: video.cover_preview_url,
          } as VideoPrompt);
        } else {
          updateAsset(targetIdx, uploadFile.file as ImagePrompt);
        }
      }
    };

    const handleReferenceModalMediaDrop = useMemoizedFn(
      (file: UploadFile | AudioUploadFile, type: 'image' | 'video' | 'audio') => {
        if (type === 'audio') return;
        const uploadFile = file as UploadFile;
        if (uploadFile.status === 'uploading') {
          const isFirstFile = !uploadFile.batchIndex || uploadFile.batchIndex === 0;
          if (isFirstFile) {
            modalDropAnchorRef.current = findAvailableSlot(0);
            if (modalDropAnchorRef.current === -1) {
              return;
            }
          }
          const anchor = modalDropAnchorRef.current;
          if (anchor === null) return;
          handleChangeFiles(anchor, uploadFile, type);
          return;
        }
        const anchor = modalDropAnchorRef.current ?? 0;
        handleChangeFiles(anchor, uploadFile, type);
      },
    );

    // 交换资源
    const handleSwapAssets = useMemoizedFn((sourceIdx: number, targetIdx: number) => {
      const swappedAssets = state?.optionalAssets?.map((it: ImagePrompt | VideoPrompt | null, index: number) => {
        if (index === sourceIdx) return state?.optionalAssets?.[targetIdx] || null;
        if (index === targetIdx) return state?.optionalAssets?.[sourceIdx] || null;
        return it;
      });
      setState({ optionalAssets: swappedAssets });
    });

    const renderUploader = (idx: number) => {
      const currentMaxCount = isVideoMode ? 1 : MAX_IMAGE_COUNT;
      const emptySlotsCount =
        currentMaxCount - (state?.optionalAssets?.filter((it: ImagePrompt | VideoPrompt | null) => it)?.length || 0);

      const isCropLoading = preparingCropIndex === idx;
      const operations = [
        {
          key: 'select',
          icon: isCropLoading ? <Loading className="animate-spin sm:hidden" /> : <BaseLineCut className="sm:hidden" />,
          text: t('elements.elementDialog.optional'),
          onClick: () => handleClickCropButton(idx, state?.optionalAssets?.[idx]),
          disabled: isCropLoading,
        },
      ];

      const asset = state?.optionalAssets?.[idx] || null;

      return (
        <MediaUploaderWithPreview
          maxCount={emptySlotsCount}
          draggable={!isVideoMode && !uploadingIndexArr.some((i) => i.targetIdx === idx)}
          idx={idx}
          media={asset}
          onChange={(file, type) => handleChangeFiles(idx, file, type)}
          className="aspect-square w-full bg-system-black24"
          scene={UPLOAD_SCENE.MATERIAL}
          getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
          placeholder={idx ? `(${t('elements.elementDialog.optional')})` : ' '}
          operations={!isVideoMode ? operations : undefined}
          uploaderClassName="hover:bg-system-white16"
          innerClassName={cn('bg-system-white08', idx === 0 ? 'rounded-16' : 'rounded-12')}
          mediaClassName={cn(idx === 0 ? 'rounded-16' : 'rounded-12')}
          uploadIcon={<BaseLinePlus className={cn('text-3xl text-system-white', idx && 'text-xl')} />}
          onItemsReorder={!isVideoMode ? handleSwapAssets : undefined}
          loading={uploadingIndexArr.some((i) => i.targetIdx === idx)}
          closeIconClassName="text-base"
          maxVideoDuration={MAX_VIDEO_DURATION}
          minVideoDuration={MIN_VIDEO_DURATION}
          captureCoverImage={isVideoMode}
          uploadCoverImage={isVideoMode}
          acceptImage={!isVideoMode}
          acceptVideo={isVideoMode}
          mediaType={isVideoMode ? 'video' : 'image'}
          allowTempMode={true}
        />
      );
    };

    const { run: updateStyle, flush: flushUpdateStyle } = useDebounceFn(
      (style?: string) => {
        setState({ style });
      },
      {
        wait: UPDATE_TIME,
      },
    );

    const { run: updateDescription, flush: flushUpdateDescription } = useDebounceFn(
      (description?: string) => {
        setState({ description });
      },
      {
        wait: UPDATE_TIME,
      },
    );

    // 获取资源预览图
    const getAssetPreview = (it: ImagePrompt | VideoPrompt | null) => {
      if (!it) return null;
      if (isImageAsset(it)) {
        return it.selectedImagePreview || it.sourceImage;
      }
      return it.videoPreview;
    };

    // 计算是否可以进入下一步
    const enableToStep2 = useMemo(() => {
      const hasReference = state?.optionalAssets?.[0];
      const hasVoice =
        isVideoMode ||
        (state as ImageFormState)?.voice?.id ||
        (state as ImageFormState)?.voice?.id === ELEMENT_NO_VOICE_ID;
      return (
        hasReference &&
        state?.name?.trim()?.length &&
        (state?.name?.trim()?.length || 0) <= MAX_INPUT_LENGTH &&
        !checkLoading &&
        !nameCheckMsg &&
        hasVoice
      );
    }, [state, checkLoading, nameCheckMsg, isVideoMode]);

    // 计算是否可以确认
    const enableConfirmAction = useMemo(() => {
      const { style, description } = state;
      const hasStyle = style?.trim().length;
      return !hasStyle || !description?.trim().length || submitLoading;
    }, [state.style, state.description, submitLoading]);

    // 处理保存
    const handleSave = () => {
      const { style, description, optionalAssets, ...restState } = state || {};
      const voice = isVideoMode ? undefined : (state as ImageFormState).voice;

      submit({
        ...restState,
        name: state.name?.trim(),
        components: convertAssets2Components(optionalAssets) || [],
        voice,
        labels: state.labels,
        type: 'user' as const,
        recaption: {
          description,
          style,
        },
      });

      reportEvent('element_click', {
        element_name: 'confirm',
      });
    };

    useEffect(() => {
      switch (step) {
        case 1: {
          reportEvent('element_show', {
            element_name: 'next_step',
          });
          break;
        }
        case 2:
          reportEvent('element_show', {
            element_name: 'confirm',
          });
          break;
        default:
          break;
      }
    }, [step]);

    const currentMaxMediaSlots = isVideoMode ? 1 : MAX_IMAGE_COUNT;
    const filledMediaCount = state?.optionalAssets?.filter((it) => it)?.length ?? 0;
    const remainingMediaSlots = Math.max(0, currentMaxMediaSlots - filledMediaCount);
    const isMediaDropZoneDisabled = remainingMediaSlots <= 0;

    return (
      <>
        <div id="element-main" className="hide-scrollbar flex-1 overflow-y-scroll sm:pb-[120px]">
          <StepsBar steps={steps} currentStep={step} />
          {/* 编辑模式的第一步：上传资源 */}
          {step === 1 && (
            <MediaDropZone
              className="flex min-h-0 w-full min-w-0 flex-1 flex-col"
              acceptImage={!isVideoMode}
              acceptVideo={isVideoMode}
              maxImageCount={isVideoMode ? 0 : remainingMediaSlots}
              maxVideoCount={isVideoMode ? remainingMediaSlots : 0}
              disabled={isMediaDropZoneDisabled}
              scene={UPLOAD_SCENE.MATERIAL}
              getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
              maxVideoDuration={MAX_VIDEO_DURATION}
              minVideoDuration={MIN_VIDEO_DURATION}
              captureCoverImage={isVideoMode}
              uploadCoverImage={isVideoMode}
              allowTempMode={true}
              onChange={handleReferenceModalMediaDrop}
              ignoreModalOpenGuard={true}
            >
              <div className="mt-3 flex flex-col items-center">
                <div className={cn('mb-2 w-[345px] text-system-white/40 text-xs', isVideoMode && 'w-[200px]')}>
                  {t('elements.elementDialog.mainReference')}
                </div>
                <div className="flex h-[200px]">
                  <div className={cn('flex aspect-square h-full', !isVideoMode && 'mr-6')}>{renderUploader(0)}</div>

                  {!isVideoMode && (
                    <div className="box-content flex w-[96px] flex-col gap-2 border-system-white08 border-l pl-6">
                      {renderUploader(1)}
                      {renderUploader(2)}
                    </div>
                  )}
                </div>
                {SITE === 'china' && (
                  <p className={cn('mt-2 w-[345px] text-system-white/40 text-xs', isVideoMode && 'w-[200px]')}>
                    限使用自有版权或已获授权的{isVideoMode ? '视频' : '图片'}。
                  </p>
                )}
              </div>
              <div className="mt-6 flex flex-col gap-3 px-4">
                <div className="flex flex-col gap-2">
                  <div className="text-system-white text-xs">{t('elements.elementDialog.name')}</div>
                  <InputField
                    key={`name-${state.modality}-${isEditMode ? 'edit' : 'create'}`}
                    initialValue={state?.name}
                    ref={nameRef}
                    fieldClassName="bg-system-black32 border-none rounded-12 h-12"
                    readOnly={false}
                    placeholder={t('elements.elementDialog.namePlaceholder')}
                    onChange={(e) => {
                      setNameCheckMsg('');
                      setState({ name: e.target.value });
                    }}
                    checking={checkLoading}
                    errMsg={nameCheckMsg}
                    maxLength={MAX_INPUT_LENGTH}
                  />
                </div>

                {!isVideoMode && (
                  <div className="flex flex-col gap-2">
                    <div className="text-system-white text-xs">{t('elements.elementDialog.sound')}</div>
                    <VoiceSelectionDialog
                      open={isAudioDialogOpen}
                      onOpenChange={setIsAudioDialogOpen}
                      title={t('elements.elementDialog.sound')}
                      value={currentVoice?.id ?? ELEMENT_NO_VOICE_ID}
                      allowNoVoice
                      noSoundLabel={t('elements.elementDialog.noSound')}
                      voiceInfoEditFieldSuffix="element"
                      trigger={
                        <button
                          type="button"
                          className="h-11 w-full rounded-12 bg-system-black36 px-3 py-2 text-left text-system-white36"
                        >
                          {currentVoice && (currentVoice.id || currentVoice.id === ELEMENT_NO_VOICE_ID) ? (
                            <DisplaySelectedAudioItem
                              audio={currentVoice}
                              className="rounded-full bg-transparent px-0 py-0"
                              audioList={TTS_VOICE_LIST}
                            />
                          ) : (
                            t('elements.elementDialog.soundPlaceholder')
                          )}
                        </button>
                      }
                      onConfirm={(voiceId, voice) => {
                        setState((prev) => {
                          if (prev.modality === 'video') {
                            return prev;
                          }

                          if (voiceId === ELEMENT_NO_VOICE_ID || !voice) {
                            return {
                              ...prev,
                              voice: { id: ELEMENT_NO_VOICE_ID },
                            };
                          }

                          return {
                            ...prev,
                            voice,
                          };
                        });
                      }}
                    />
                  </div>
                )}

                {/* 标签选择 */}
                <div className="flex flex-col gap-2">
                  <div className="text-system-white text-xs">{t('elements.elementDialog.tag')}</div>
                  <ChooseLabelSelect
                    key={`label-${state.modality}-${isEditMode ? 'edit' : 'create'}`}
                    onAdd={(label: MaterialV1Label) => {
                      handleAddLabel(label);
                      setState({ labels: [label] });
                    }}
                    onChange={(id: string) => {
                      const selectedLabel = labels?.find((label) => label.id === id);
                      setState({ labels: selectedLabel ? [selectedLabel] : [] });
                    }}
                    value={state?.labels?.[0]?.id}
                    options={labels}
                    placeholder={t('elements.elementDialog.tagPlaceholder')}
                  />
                </div>
              </div>
            </MediaDropZone>
          )}
          {step === 2 && (
            <div className="mt-3 flex flex-col items-center">
              <div className="flex flex-col">
                <div className="mb-2 text-system-white/40 text-xs">{t('elements.elementDialog.mainReference')}</div>
                <div className="flex flex-1 items-end gap-6">
                  <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-12 bg-system-white04">
                    {state?.optionalAssets?.[0] ? (
                      (() => {
                        const asset = state.optionalAssets[0];
                        const imageSrc = getAssetPreview(asset);
                        return imageSrc ? (
                          <MediaWithMaskBg
                            className="h-full w-full object-contain object-center"
                            src={imageSrc}
                            type={isImageAsset(asset) ? 'image' : 'video'}
                          />
                        ) : (
                          <div className="text-xs">{t('elements.elementDialog.noUpload')}</div>
                        );
                      })()
                    ) : (
                      <div className="text-xs">{t('elements.elementDialog.noUpload')}</div>
                    )}
                  </div>
                  {!isVideoMode && (
                    <div className="flex items-end gap-2 border-l border-l-system-white08 pl-6 sm:pl-4">
                      {state?.optionalAssets?.slice(1)?.map((asset, index) => {
                        const slotKey = `slot-${index + 1}`;
                        if (!asset) {
                          return (
                            <div
                              key={slotKey}
                              className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-12 bg-system-white04"
                            >
                              <div className="text-xs">{t('elements.elementDialog.noUpload')}</div>
                            </div>
                          );
                        }
                        const imageSrc = getAssetPreview(asset);
                        return (
                          <div
                            key={slotKey}
                            className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-12 bg-system-white04"
                          >
                            {imageSrc ? (
                              <MediaWithMaskBg
                                className="h-full w-full object-contain object-center"
                                src={imageSrc}
                                type={isImageAsset(asset) ? 'image' : 'video'}
                              />
                            ) : (
                              <div className="text-xs">{t('elements.elementDialog.noUpload')}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="px-4 pt-6">
                <div className="mb-3 flex items-start gap-2 rounded-6 border border-system-blue02 bg-[rgba(0,134,255,0.32)] px-4 py-2 text-sm">
                  <BaseFillExclamation className="mt-0.5 shrink-0 text-base text-system-blue02" />
                  {t('elements.elementDialog.recaptionTip')}
                </div>
                <TextAreaField
                  className="mb-3"
                  initialValue={state?.style}
                  labelClassName="sm:text-sm"
                  onChange={(e) => updateStyle(e.target.value)}
                  placeholder={t('elements.elementDialog.stylePlaceholder')}
                  label={t('elements.elementDialog.style')}
                  rows={2}
                  fieldClassName={cn('hide-scrollbar', 'sm:bg-system-black16')}
                  readOnly={step !== 2}
                  required
                  maxLength={MAX_TEXTAREA_INPUT_LENGTH.style}
                  errorMsg={!state.style?.trim()?.length ? t('elements.elementDialog.required') : ''}
                  checkChangeOnBlur={(change) => {
                    flushUpdateStyle();
                    change &&
                      reportEvent('description_modification', {
                        modification_position: 'style',
                      });
                  }}
                />
                <TextAreaField
                  rows={5}
                  labelClassName="sm:text-sm"
                  initialValue={state?.description}
                  onChange={(e) => updateDescription(e.target.value)}
                  placeholder={t('elements.elementDialog.descriptionPlaceholder')}
                  label={t('elements.elementDialog.description')}
                  fieldClassName={cn('hide-scrollbar', 'sm:bg-system-black16')}
                  readOnly={step !== 2}
                  required
                  errorMsg={!state.description?.trim()?.length ? t('elements.elementDialog.required') : ''}
                  maxLength={MAX_TEXTAREA_INPUT_LENGTH.description}
                  checkChangeOnBlur={(change) => {
                    flushUpdateDescription();
                    change &&
                      reportEvent('description_modification', {
                        modification_position: 'description',
                      });
                  }}
                />
              </div>
            </div>
          )}
          <CharImgCropModal
            title={t('create.createForm.upload.selectObject')}
            description={t('create.createForm.upload.selectObjectTip')}
            imageSource={selectModalImageSource}
            imagePrompts={
              state?.optionalAssets
                ?.filter(
                  (asset: ImagePrompt | VideoPrompt | null): asset is ImagePrompt =>
                    asset !== null && isImageAsset(asset),
                )
                ?.map((asset: ImagePrompt | VideoPrompt | null) => {
                  if (!asset || !isImageAsset(asset)) return null;
                  return {
                    id: asset.sourceImage,
                    status: 'done' as const,
                    prompt: asset,
                  };
                }) || []
            }
            onCancel={handleCloseSelectModal}
            onConfirm={handleSaveChangedImagePrompts}
            handleClickImagePrompt={handleOpenSelectModal}
            handleRemoveImage={(image: ImagePrompt, idx: number) => {
              // 只对图片处理预览 URL
              if (image.sourceImagePreview) URL.revokeObjectURL(image.sourceImagePreview);
              if (image.selectedImagePreview) URL.revokeObjectURL(image.selectedImagePreview);

              handleChangeFiles(
                idx,
                {
                  id: idx.toString(),
                  file: null,
                  status: 'removed',
                },
                'image',
              );
            }}
            maxImageSize={maxImageSize}
            scene={UPLOAD_SCENE.MATERIAL}
            replaceImage={(idx: number, file: UploadFile<UploadImageType>) => {
              if (file.status === 'done') {
                updateAsset(idx, file.file as ImagePrompt);
                return file.file;
              }
              return null;
            }}
            handleExchange={handleSwapAssets}
            allowTempMode={true}
          />
        </div>
        <DialogFooter
          className={cn(
            'mt-6 block shrink-0 space-x-0 px-6 pb-0 sm:fixed sm:right-0 sm:bottom-0 sm:left-0 sm:mt-0 sm:bg-[#191D25] sm:py-4',
          )}
        >
          <div className="flex h-11 w-full justify-center gap-3">
            {step === 1 && (
              <Button
                className="h-full min-w-[184px] rounded-12 disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24 sm:flex-1"
                variant="solid"
                disabled={!enableToStep2 || submitLoading}
                onClick={async () => {
                  if (!preCreateLoading) {
                    const isValid = await checkName();
                    if (isValid) {
                      afterNameCheck();
                    }
                  }
                }}
              >
                {t('elements.elementDialog.nextStep')}
                {preCreateLoading ? (
                  <Loading className="ml-2 h-5 w-5 animate-spin text-black" />
                ) : (
                  <BaseLineRight className="ml-2 h-5 w-5" />
                )}
              </Button>
            )}
            {step === 2 && (
              <>
                <Button
                  variant="default"
                  className="h-full min-w-[120px] rounded-12 bg-system-hover01"
                  disabled={submitLoading}
                  onClick={() => setStep(1)}
                >
                  <BaseLineLeft className="mr-2 h-5 w-5" />
                  {t('elements.elementDialog.lastStep')}
                </Button>
                <Button
                  variant="solid"
                  className="flex min-h-full min-w-[184px] rounded-12 disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24"
                  disabled={enableConfirmAction}
                  onClick={handleSave}
                >
                  {t('elements.elementDialog.confirm')}
                  {submitLoading && <Loading className="ml-2 h-5 w-5 animate-spin text-black" />}
                </Button>
              </>
            )}
          </div>
          <DialogFooterTip />
        </DialogFooter>
      </>
    );
  },
);

MediaElementForm.displayName = 'MediaElementForm';
